import { determinant, SINGULAR_EPSILON, type Matrix } from '../geometry/matrix';
import { segmentsBounds, tryParsePath } from '../geometry/path';
import { projectSchema } from './schema';
import { DOCUMENT_LIMITS as L, type ProjectDocument, type VectorNode } from './types';

export interface ValidationIssue {
  /** Dotted location such as `designs.0.nodes.2.transform`. */
  readonly path: string;
  readonly message: string;
  readonly code:
    | 'schema'
    | 'duplicate-id'
    | 'missing-reference'
    | 'singular-transform'
    | 'invalid-path'
    | 'too-complex'
    | 'too-deep'
    | 'invalid-shape'
    | 'duplicate-finger';
}

export type ValidationResult<T> =
  | { readonly ok: true; readonly value: T }
  | { readonly ok: false; readonly issues: readonly ValidationIssue[] };

/**
 * Full validation for untrusted project data: structural schema first, then
 * semantic checks. JSON Schema passing alone never establishes safety (docs/09).
 */
export function parseProjectDocument(input: unknown): ValidationResult<ProjectDocument> {
  const parsed = projectSchema.safeParse(input);
  if (!parsed.success) {
    return {
      ok: false,
      issues: parsed.error.issues.slice(0, 50).map((issue) => ({
        path: issue.path.map(String).join('.'),
        message: issue.message,
        code: 'schema' as const,
      })),
    };
  }
  const issues = validateProjectSemantics(parsed.data);
  return issues.length > 0 ? { ok: false, issues } : { ok: true, value: parsed.data };
}

export function validateProjectSemantics(doc: ProjectDocument): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const designIds = new Set<string>();
  const nodeIds = new Set<string>();
  let totalNodes = 0;
  let totalCommands = 0;

  const checkMatrix = (m: Matrix, path: string) => {
    if (Math.abs(determinant(m)) <= SINGULAR_EPSILON) {
      issues.push({
        path,
        message: 'Transform is singular (zero scale).',
        code: 'singular-transform',
      });
    }
  };

  const walk = (node: VectorNode, path: string, depth: number) => {
    totalNodes += 1;
    if (depth > L.maxNestingDepth) {
      issues.push({
        path,
        message: `Nesting deeper than ${L.maxNestingDepth} levels.`,
        code: 'too-deep',
      });
      return;
    }
    if (nodeIds.has(node.id)) {
      issues.push({
        path: `${path}.id`,
        message: `Duplicate node ID "${node.id}".`,
        code: 'duplicate-id',
      });
    }
    nodeIds.add(node.id);
    checkMatrix(node.transform, `${path}.transform`);
    if (node.type === 'path') {
      const result = tryParsePath(node.d, { maxCommands: L.maxPathCommands });
      if (!result.ok) {
        issues.push({ path: `${path}.d`, message: result.error.message, code: 'invalid-path' });
      } else {
        totalCommands += result.segments.length;
      }
    } else {
      node.children.forEach((child, i) => walk(child, `${path}.children.${i}`, depth + 1));
    }
  };

  doc.designs.forEach((design, di) => {
    const path = `designs.${di}`;
    if (designIds.has(design.id)) {
      issues.push({
        path: `${path}.id`,
        message: `Duplicate design ID "${design.id}".`,
        code: 'duplicate-id',
      });
    }
    designIds.add(design.id);
    design.nodes.forEach((node, ni) => walk(node, `${path}.nodes.${ni}`, 1));
  });

  if (totalNodes > L.maxTotalNodes) {
    issues.push({
      path: 'designs',
      message: `More than ${L.maxTotalNodes} nodes.`,
      code: 'too-complex',
    });
  }
  if (totalCommands > L.maxTotalPathCommands) {
    issues.push({
      path: 'designs',
      message: `More than ${L.maxTotalPathCommands} path commands.`,
      code: 'too-complex',
    });
  }

  const nailIds = new Set<string>();
  const fingers = new Set<string>();
  doc.nails.forEach((nail, i) => {
    const path = `nails.${i}`;
    if (nailIds.has(nail.id)) {
      issues.push({
        path: `${path}.id`,
        message: `Duplicate nail ID "${nail.id}".`,
        code: 'duplicate-id',
      });
    }
    nailIds.add(nail.id);
    const fingerKey = `${nail.hand}:${nail.finger}`;
    if (fingers.has(fingerKey)) {
      issues.push({
        path: `${path}.finger`,
        message: `Two nails are assigned to the ${nail.hand} ${nail.finger}.`,
        code: 'duplicate-finger',
      });
    }
    fingers.add(fingerKey);
    if (!designIds.has(nail.designId)) {
      issues.push({
        path: `${path}.designId`,
        message: `Nail references missing design "${nail.designId}".`,
        code: 'missing-reference',
      });
    }
    checkMatrix(nail.placement, `${path}.placement`);
    const shapeIssue = validateNailShapePath(nail.shape.normalizedPathD);
    if (shapeIssue)
      issues.push({
        path: `${path}.shape.normalizedPathD`,
        message: shapeIssue,
        code: 'invalid-shape',
      });
  });

  return issues;
}

/** A nail silhouette must parse, be closed, and have a nonzero area-bearing bounding box. */
export function validateNailShapePath(d: string): string | null {
  const parsed = tryParsePath(d, { maxCommands: 2000 });
  if (!parsed.ok) return parsed.error.message;
  const segments = parsed.segments;
  if (segments.length === 0 || segments[segments.length - 1]!.type !== 'Z') {
    return 'Nail shape must be a closed path.';
  }
  if (segments.filter((s) => s.type === 'M').length !== 1) {
    return 'Nail shape must be a single closed outline.';
  }
  const bounds = segmentsBounds(segments);
  if (!bounds || bounds.width <= 1e-9 || bounds.height <= 1e-9) {
    return 'Nail shape has no area.';
  }
  return null;
}
